import React, { useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import apiEndpoints from "@store/apiEndpoints";
import useApiResource from "./useApiResource";
import useCaseList, { brandParams } from "./useCaseList";
import { DEFAULT_PERIOD, PERIODS } from "./config/periods";
import { resolveCardFilter } from "./config/cardFilters";
import TicketNewHeader from "./sections/TicketNewHeader";
import PerformanceSection from "./sections/PerformanceSection";
import TeamVelocitySection from "./sections/TeamVelocitySection";
import CaseSnapshotSection from "./sections/CaseSnapshotSection";
import NeedsAttentionSection from "./sections/NeedsAttentionSection";
import CaseListSection from "./sections/CaseListSection";
import "./TicketNew.css";

const PERIOD_LABELS = {
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  ytd: "Year to date",
};

/*
RMA Portal - case dashboard.

Four requests back this page:
  - dashboard/performance-overview/  (period-scoped)
  - dashboard/team-velocity/         (period-scoped)
  - dashboard/case-snapshot/         (period-scoped; feeds BOTH the KPI row and
                                      the SLA cards - one request, two blocks)
  - tickets/                         (the case list, brand-scoped)

The period pills refetch performance, velocity and the snapshot. Within the
snapshot response, only `cases.open.net_change` and `cases.completed` actually
move with the period - the rest (current open/pending/DOA counts and every SLA
figure) describe the live state and stay the same regardless of `period`.

Click-to-filter (RMAS-129): every dashboard card narrows the case list to the
tickets it counts, using the `filters` dict the dashboard returns for it. The
dashboard and the list share one TicketFilter on the backend, so the counts
only line up if both are sent the same scope params - hence `scope` below goes
to all four requests. `period` stays dashboard-only; the date range is already
inside each card's `filters`.
*/

// This dashboard always shows tickets across every brand the user has access
// to, regardless of the single brand selected in the navbar.
const BRAND_SCOPE = "all";

const isPeriod = function (value) {
  return PERIODS.some(function (p) {
    return p.key === value;
  });
};

const TicketNew = function (props) {
  const { protectedFetch, selectedbrandID, userInfo } = props;
  const history = useHistory();
  const location = useLocation();
  const listRef = useRef(null);

  /*
  The period and the clicked card live in the URL (`?period=7d&card=...`), so a
  refresh or a back-navigation from a case detail keeps the list filtered. The
  period has to travel with the card: the card's date range follows it, and a
  refresh falling back to the default period would change the range.
  Defaults are left out to keep the URL clean.
  */
  const urlParams = new URLSearchParams(location.search);
  const period = isPeriod(urlParams.get("period"))
    ? urlParams.get("period")
    : DEFAULT_PERIOD;
  // Card id - see config/cardFilters.js.
  const cardId = urlParams.get("card") || null;

  const updateUrl = function (changes) {
    const next = new URLSearchParams(location.search);
    Object.keys(changes).forEach(function (key) {
      if (changes[key]) next.set(key, changes[key]);
      else next.delete(key);
    });
    const search = next.toString();
    if (search === urlParams.toString()) return;
    history.replace({ pathname: location.pathname, search });
  };
  const setPeriod = function (next) {
    updateUrl({ period: next === DEFAULT_PERIOD ? null : next });
  };

  // Non-admins only get the case list below - the header and the analytics
  // widgets are an admin-only view of the same brand-wide data.
  const isAdmin = userInfo?.user?.role === "Admin";

  const scope = brandParams(selectedbrandID, BRAND_SCOPE);

  const performance = useApiResource(
    protectedFetch,
    apiEndpoints.dashboard.performanceOverview(),
    { period, ...scope },
    { skip: !isAdmin },
  );
  const velocity = useApiResource(
    protectedFetch,
    apiEndpoints.dashboard.teamVelocity(),
    { period, ...scope },
    { skip: !isAdmin },
  );
  const snapshot = useApiResource(
    protectedFetch,
    apiEndpoints.dashboard.caseSnapshot(),
    { period, ...scope },
    { skip: !isAdmin },
  );

  // Re-read from the latest responses each render, so a period change moves
  // the active card's date range along with the number on the card.
  // `null` until the dashboard responses that carry this card have loaded -
  // after a refresh the list waits on the default view until then.
  const cardFilter = cardId
    ? resolveCardFilter(cardId, { performance, velocity, snapshot }, period)
    : null;

  const list = useCaseList(
    protectedFetch,
    selectedbrandID,
    BRAND_SCOPE,
    cardFilter ? cardFilter.filters : null,
  );

  // The card in the URL doesn't exist in the settled responses (a chart bucket
  // or handler not in the new period, a stale/hand-edited link, or a non-admin
  // who gets no dashboard) - drop it rather than keep a dead param around.
  const dashboardSettled =
    !performance.loading && !velocity.loading && !snapshot.loading;
  useEffect(
    function () {
      if (cardId && !cardFilter && dashboardSettled) {
        updateUrl({ card: null });
      }
    },
    // updateUrl is rebuilt every render; cardId covers what it reads.
    [cardId, cardFilter, dashboardSettled],
  );

  const selectCardFilter = function (id) {
    // Search would narrow the list below the card's count - start clean.
    list.clearFilters();
    updateUrl({ card: id });
    if (listRef.current && listRef.current.scrollIntoView) {
      listRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  const clearCardFilter = function () {
    updateUrl({ card: null });
  };
  const activeFilterId = cardFilter ? cardId : null;

  // The detail route fetches GET /ticket/{id}/, which takes the numeric PK -
  // not the human-readable ticket_number - so that's what has to go in the URL.
  const openCase = function (row) {
    if (!row || !row.id) return;
    history.push("/ticket-new/" + encodeURIComponent(row.id));
  };

  return (
    <div className="ticket-new-root">
      <div className="tn-page">
        {isAdmin && (
          <>
            <TicketNewHeader
              period={period}
              onPeriod={function (next) {
                if (isPeriod(next)) setPeriod(next);
              }}
              onNewCase={function () {
                history.push("/ticket-new/new-case");
              }}
            />

            <div className="tn-grid-main">
              <PerformanceSection
                resource={performance}
                periodLabelFallback={PERIOD_LABELS[period]}
                activeFilterId={activeFilterId}
                onSelectFilter={selectCardFilter}
              />
              <div className="tn-grid-side">
                <TeamVelocitySection
                  resource={velocity}
                  activeFilterId={activeFilterId}
                  onSelectFilter={selectCardFilter}
                />
              </div>
            </div>

            <CaseSnapshotSection
              resource={snapshot}
              period={period}
              activeFilterId={activeFilterId}
              onSelectFilter={selectCardFilter}
            />
            <NeedsAttentionSection
              resource={snapshot}
              activeFilterId={activeFilterId}
              onSelectFilter={selectCardFilter}
            />
          </>
        )}
        <div className="tn-caselist-anchor" ref={listRef}>
          <CaseListSection
            list={list}
            onOpen={openCase}
            cardFilter={cardFilter}
            onClearCardFilter={clearCardFilter}
          />
        </div>
      </div>
    </div>
  );
};

export default TicketNew;
